import _ from 'lodash';
import migrationRunsFixture from './data/platform-ncl/aiqum-migration-runs.json';
import migrationResourcesFixture from './data/platform-ncl/aiqum-migration-resources.json';
import dryRunSummaryFixture from './data/platform-ncl/aiqum-migration-dry-run-summary.json';
import migrationSummaryFixture from './data/platform-ncl/aiqum-migration-summary.json';

const MIGRATION_RUN_TYPE = 'application/vnd.netapp.bxp.migrationrun';
const DRY_RUN_ACCEPT = 'migrationrun-dryrunsummary';
const MIGRATION_SUMMARY_ACCEPT = 'migrationrun-migrationsummary';
const EXPORT_BODY = 'simulator-history-exporter';
const HISTORY_RETAIN_MS = 30 * 24 * 60 * 60 * 1000;

const DESIRED_STATE_TRANSITIONS = {
    DISCOVERED: ['DISCOVERING', 'DISCOVERED'],
    DRY_RUN_SUCCEEDED: ['DRY_RUNNING', 'DRY_RUN_SUCCEEDED'],
    MIGRATED: ['MIGRATING', 'MIGRATED']
};

const MIGRATION_PATH = /\/v1\/aiqum-migrator\/organizations\/[^/]+\/migrationruns(?:\/([^/]+)(?:\/(resources|exportbundle|backfilljob)(?:\/([^/]+))?)?)?\/?$/;

let runs = [];
let runSequence = 0;

const cloneResources = (migrationRunId) => _.cloneDeep(migrationResourcesFixture.resources).map(resource => ({
    ...resource,
    migrationRunId
}));

const publicRun = (run) => ({
    id: run.id,
    status: run.status,
    percentComplete: run.percentComplete,
    connection: _.cloneDeep(run.connection),
    metadata: _.cloneDeep(run.metadata),
    ...(run.historyStatus ? { historyStatus: _.cloneDeep(run.historyStatus) } : {}),
    ...(run.lastError ? { lastError: _.cloneDeep(run.lastError) } : {}),
    type: MIGRATION_RUN_TYPE,
    version: '1.0'
});

// History import has no poller, so the next read is already complete.
// Discovery, dry run, and migrate keep one in-progress status so the next poll can finish them.
const readRun = (run) => {
    if (run.pendingHistory) {
        run.historyStatus = {
            ...(run.historyStatus || {}),
            status: run.pendingHistory,
            percentComplete: 100
        };
        run.pendingHistory = null;
    }
    const visible = publicRun(run);
    if (run.pendingStatus) {
        run.status = run.pendingStatus;
        run.pendingStatus = null;
        run.percentComplete = 100;
    }
    return visible;
};

const findRun = (runId) => runs.find(run => run.id === runId);

const notFound = () => ({
    status: 404,
    body: { title: 'Migration run not found' }
});

const respond = (body, status = 200) => ({ status, body });

const listRuns = () => {
    const items = runs.map(readRun);
    return respond({
        items,
        count: items.length,
        totalCount: items.length
    });
};

const createRun = (body) => {
    const run = {
        id: `migration-run-${Date.now()}-${runSequence++}`,
        status: 'CREATED',
        percentComplete: 0,
        connection: {
            aiqumBaseUrl: body?.connection?.aiqumBaseUrl,
            username: body?.connection?.username
        },
        metadata: { creationTimestamp: new Date().toISOString() },
        resources: [],
        includeResources: false
    };
    runs.unshift(run);
    return respond(publicRun(run));
};

const patchDesiredState = (run, body) => {
    const transition = DESIRED_STATE_TRANSITIONS[body?.stateDesired];
    if (!transition) {
        return {
            status: 400,
            body: {
                title: 'Unsupported desired state',
                invalidParams: [{ name: 'stateDesired', reason: 'Unsupported desired state.' }]
            }
        };
    }
    const [immediate, next] = transition;
    run.status = immediate;
    run.pendingStatus = next;
    run.percentComplete = 40;
    run.lastError = undefined;
    if (immediate === 'DISCOVERING') {
        run.resources = cloneResources(run.id);
        run.includeResources = true;
    }
    return respond(publicRun(run));
};

const dryRunSummary = (run) => {
    const visible = readRun(run);
    return respond({
        ..._.cloneDeep(dryRunSummaryFixture),
        status: visible.status,
        blockers: visible.status === 'DRY_RUN_FAILED' ? _.cloneDeep(run.blockers || []) : [],
        ...(visible.lastError ? { lastError: visible.lastError } : {})
    });
};

const migrationSummary = (run) => {
    const visible = readRun(run);
    return respond({
        ..._.cloneDeep(run.summary || migrationSummaryFixture),
        status: visible.status
    });
};

const getRun = (run, accept) => {
    const header = (accept || '').toLowerCase();
    if (header.includes(DRY_RUN_ACCEPT)) {
        return dryRunSummary(run);
    }
    if (header.includes(MIGRATION_SUMMARY_ACCEPT)) {
        return migrationSummary(run);
    }
    return respond(readRun(run));
};

const getResources = (run) => respond({
    migrationRunId: run.id,
    resources: _.cloneDeep(run.resources || [])
});

const patchResource = (run, resourceId, body) => {
    const resource = (run.resources || []).find(item => item.id === resourceId);
    if (!resource) {
        return {
            status: 404,
            body: { title: 'Migration resource not found' }
        };
    }
    if (body?.action) {
        resource.action = body.action;
    }
    if (body?.ldapIpAddresses) {
        resource.ldapIpAddresses = body.ldapIpAddresses;
    }
    if (body?.credentials && Object.keys(body.credentials).length) {
        resource.credentialHandle = `cred-${resource.id}`;
    }
    return respond(_.cloneDeep(resource));
};

const exportBundle = () => ({
    status: 200,
    raw: true,
    headers: {
        'Content-Type': 'application/x-tar',
        'Content-Disposition': 'attachment; filename="console-history-exporter.tar"',
        'x-checksum-sha256': 'simulator-history-checksum'
    },
    body: EXPORT_BODY
});

const startBackfill = (run, status) => {
    const retryCount = status === 'RETRYING'
        ? (run.historyStatus?.retryCount || 0) + 1
        : (run.historyStatus?.retryCount || 0);
    run.historyStatus = {
        status,
        percentComplete: 20,
        retryCount,
        retainUntil: new Date(Date.now() + HISTORY_RETAIN_MS).toISOString()
    };
    run.pendingHistory = 'COMPLETED';
    return respond(_.cloneDeep(run.historyStatus));
};

const parseMigrationPath = (url = '') => {
    const path = url.split('?')[0];
    const match = path.match(MIGRATION_PATH);
    if (!match) {
        return null;
    }
    return {
        runId: match[1],
        subresource: match[2],
        resourceId: match[3]
    };
};

/**
 * Answers AIQUM migration requests for the current simulator session.
 * POST and PATCH update the in-memory run so a later GET sees the new status.
 * @param {{ method: string, url: string, accept?: string, body?: Object }} request
 * @returns {{ status: number, body: Object|string, headers?: Object, raw?: boolean }}
 */
const handleAiqumMigrationRequest = ({ method, url, accept, body }) => {
    const parsed = parseMigrationPath(url);
    if (!parsed) {
        return notFound();
    }
    const verb = (method || '').toUpperCase();
    const { runId, subresource, resourceId } = parsed;

    if (!runId) {
        if (verb === 'GET') {
            return listRuns();
        }
        if (verb === 'POST') {
            return createRun(body);
        }
        return notFound();
    }

    const run = findRun(runId);
    if (!run) {
        return notFound();
    }

    if (subresource === 'resources' && !resourceId && verb === 'GET') {
        return getResources(run);
    }
    if (subresource === 'resources' && resourceId && verb === 'PATCH') {
        return patchResource(run, resourceId, body);
    }
    if (subresource === 'exportbundle' && verb === 'GET') {
        return exportBundle();
    }
    if (subresource === 'backfilljob' && verb === 'POST') {
        return startBackfill(run, 'UPLOADED');
    }
    if (subresource === 'backfilljob' && verb === 'PATCH') {
        return startBackfill(run, 'RETRYING');
    }
    if (!subresource && verb === 'GET') {
        return getRun(run, accept);
    }
    if (!subresource && verb === 'PATCH') {
        return patchDesiredState(run, body);
    }
    return notFound();
};

const resetAiqumMigrationSimulator = () => {
    const startedAt = Date.now();
    runs = _.cloneDeep(migrationRunsFixture.items).map((item, index) => {
        const run = {
            ...item,
            connection: { ...item.connection },
            metadata: {
                creationTimestamp: new Date(startedAt - index * 60 * 1000).toISOString()
            },
            resources: item.includeResources ? cloneResources(item.id) : [],
            pendingStatus: null,
            pendingHistory: null
        };
        if (run.historyStatus) {
            run.historyStatus.retainUntil = new Date(startedAt + HISTORY_RETAIN_MS).toISOString();
        }
        return run;
    });
};

resetAiqumMigrationSimulator();

export {
    handleAiqumMigrationRequest,
    resetAiqumMigrationSimulator
};
